/**
 * Helpers for propagating the admin-created invite slug (and master-invite
 * variable tag) across pages.
 *
 * A customer arrives at `/i/<slug>` (single invite) or `/m/<slug>/<tag>` /
 * `/m/<slug>?v=<tag>` (master invite). InviteLanding stashes both values in
 * sessionStorage (survives full reloads on the same tab) AND appends
 * `?invite=<slug>&v=<tag>` when redirecting into shared pages so downstream
 * code (MobileCaptureDialog, doTrackedRedirect, ProductCard) can attach them
 * to their `/customers/identify` and `/track/click` payloads.
 *
 * Uses sessionStorage (not localStorage) so a different customer opening a
 * different invite on the same device doesn't inherit the wrong context.
 */

const KEY = "hitch:invite_slug";
const TAG_KEY = "hitch:invite_tag";

// In-memory fallbacks for strict Incognito modes where sessionStorage throws.
let _mem = null;
let _memTag = null;

export function setInviteSlug(slug) {
  const v = (slug || "").trim();
  if (!v) return;
  try {
    sessionStorage.setItem(KEY, v);
  } catch (_) {
    _mem = v;
  }
}

export function setInviteTag(tag) {
  const v = (tag || "").trim();
  if (!v) return;
  try {
    sessionStorage.setItem(TAG_KEY, v);
  } catch (_) {
    _memTag = v;
  }
}

export function getInviteSlug() {
  // Priority: explicit ?invite= in current URL > sessionStorage > in-memory.
  try {
    const q = new URLSearchParams(window.location.search).get("invite");
    if (q) return q;
  } catch (_) { /* ignore */ }
  try {
    const v = sessionStorage.getItem(KEY);
    if (v) return v;
  } catch (_) { /* ignore */ }
  return _mem;
}

export function getInviteTag() {
  // Priority: explicit ?v= in current URL > sessionStorage > in-memory.
  try {
    const q = new URLSearchParams(window.location.search).get("v");
    if (q) return q;
  } catch (_) { /* ignore */ }
  try {
    const v = sessionStorage.getItem(TAG_KEY);
    if (v) return v;
  } catch (_) { /* ignore */ }
  return _memTag;
}

export function clearInviteSlug() {
  try { sessionStorage.removeItem(KEY); } catch (_) { /* ignore */ }
  try { sessionStorage.removeItem(TAG_KEY); } catch (_) { /* ignore */ }
  _mem = null;
  _memTag = null;
}

/** Append `?invite=<slug>` (and `&v=<tag>` when present) to any URL if we
 *  currently have an invite context. Preserves existing query params.
 *  Returns the input unchanged when there is no invite in play so callers
 *  can use it unconditionally. */
export function withInviteParam(url) {
  const slug = getInviteSlug();
  if (!slug || !url) return url;
  const tag = getInviteTag();
  try {
    const u = new URL(url, window.location.origin);
    if (!u.searchParams.has("invite")) u.searchParams.set("invite", slug);
    if (tag && !u.searchParams.has("v")) u.searchParams.set("v", tag);
    return u.pathname + "?" + u.searchParams.toString() + u.hash;
  } catch (_) {
    // Non-URL string — bail without touching it.
    return url;
  }
}
